import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowLeft, ArrowRight, Save, Check,
  Bot, Mic, Palette, MessageSquare, Volume2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_VOICES = [
  { id: "21m00Tcm4TlvDq8ikWAM", name: "Rachel",  style: "Calm, Narrator",      gender: "F" },
  { id: "pNInz6obpgDQGcFmaJgB", name: "Adam",    style: "Deep, Narrator",      gender: "M" },
  { id: "EXAVITQu4vr4xnSDxMaL", name: "Bella",   style: "Soft, Young",         gender: "F" },
  { id: "ErXwobaYiN019PkySvjV", name: "Antoni",  style: "Well-rounded",        gender: "M" },
  { id: "MF3mGyEYCl7XYWbV9V6O", name: "Elli",    style: "Young, Emotional",    gender: "F" },
  { id: "TxGEqnHWrfWFTfGW9XjX", name: "Josh",    style: "Deep, Authoritative", gender: "M" },
  { id: "VR6AewLTigWG4xSOukaG", name: "Arnold",  style: "Husky, Commanding",   gender: "M" },
  { id: "yoZ06aMxZJJ28mfd3POQ", name: "Sam",     style: "Raspy, Casual",       gender: "M" },
  { id: "AZnzlk1XvdvUeBnXmlld", name: "Domi",    style: "Strong, Assertive",   gender: "F" },
  { id: "LcfcDJNUP1GQjkzn1xUU", name: "Emily",   style: "Calm, Whispery",      gender: "F" },
];

const PRESET_COLORS = [
  '#00D67E', '#3B82F6', '#8B5CF6', '#F59E0B',
  '#EF4444', '#EC4899', '#06B6D4', '#F97316',
];

const STEPS = [
  { id: 'basics',  label: 'Basics',  icon: Bot },
  { id: 'voice',   label: 'Voice',   icon: Mic },
  { id: 'widget',  label: 'Widget',  icon: Palette },
];

/* ── Widget preview ──────────────────────────────────────────── */
function WidgetPreview({ form }) {
  const color = form.widget_color || '#00D67E';
  return (
    <div className="relative select-none" style={{ height: 340 }}>
      {/* Mock browser bar */}
      <div className="rounded-xl overflow-hidden border" style={{ borderColor: 'var(--color-border)', height: '100%', background: 'var(--color-base)' }}>
        <div className="flex items-center gap-1.5 px-4 py-3 border-b" style={{ borderColor: 'var(--color-border)', background: 'var(--color-surface)' }}>
          <span className="w-2.5 h-2.5 rounded-full bg-red-400/60" />
          <span className="w-2.5 h-2.5 rounded-full bg-yellow-400/60" />
          <span className="w-2.5 h-2.5 rounded-full bg-green-400/60" />
          <span className="ml-3 text-xs font-mono px-3 py-0.5 rounded" style={{ background: 'var(--color-elevated)', color: 'var(--color-text-muted)' }}>
            yourwebsite.com
          </span>
        </div>
        <div className="relative" style={{ height: 'calc(100% - 44px)' }}>
          {/* Fake page content */}
          <div className="p-5 space-y-2">
            {[80, 60, 70, 45].map((w, i) => (
              <div key={i} className="h-2 rounded-full" style={{ width: `${w}%`, background: 'var(--color-elevated)' }} />
            ))}
          </div>
          {/* Widget panel preview */}
          <div className="absolute bottom-10 right-3 w-52 rounded-xl shadow-2xl overflow-hidden"
            style={{ background: '#0C1117', border: '1px solid rgba(255,255,255,0.08)' }}>
            <div className="px-3 py-2 flex items-center gap-2 border-b" style={{ borderColor: 'rgba(255,255,255,0.07)', background: `rgba(${hexToRgbStr(color)},0.08)` }}>
              <div className="w-2 h-2 rounded-full" style={{ background: color }} />
              <span className="text-xs font-medium text-white truncate">{form.widget_title || 'Voice Assistant'}</span>
            </div>
            <div className="p-3 space-y-2">
              <div className="rounded-lg px-2.5 py-1.5 text-xs max-w-[85%]" style={{ background: 'rgba(255,255,255,0.06)', color: '#c9d6e3' }}>
                {form.welcome_message || 'Hello! How can I help?'}
              </div>
            </div>
          </div>
          {/* Widget button */}
          <div className="absolute bottom-3 right-3 w-9 h-9 rounded-full flex items-center justify-center shadow-lg"
            style={{ background: color, boxShadow: `0 4px 16px rgba(${hexToRgbStr(color)},0.5)` }}>
            <MessageSquare size={16} color="#000" />
          </div>
        </div>
      </div>
    </div>
  );
}

function hexToRgbStr(hex) {
  try {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `${r},${g},${b}`;
  } catch { return '0,214,126'; }
}

/* ── Field components ────────────────────────────────────────── */
function Field({ label, children, hint }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
        {label}
      </label>
      {children}
      {hint && <p className="text-xs mt-1.5" style={{ color: 'var(--color-text-muted)' }}>{hint}</p>}
    </div>
  );
}

/* ── Main component ──────────────────────────────────────────── */
export default function AgentBuilder() {
  const { id } = useParams();
  const isEdit = !!id;
  const { api } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [previewingVoice, setPreviewingVoice] = useState(null);
  const audioRef = useRef(null);

  const previewVoice = async (e, voiceId) => {
    e.stopPropagation();
    if (previewingVoice === voiceId) {
      audioRef.current?.pause();
      setPreviewingVoice(null);
      return;
    }
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setPreviewingVoice(voiceId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/voices/${voiceId}/preview`);
      if (!res.ok) throw new Error('unavailable');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => { setPreviewingVoice(null); URL.revokeObjectURL(url); };
      audio.onerror = () => { setPreviewingVoice(null); URL.revokeObjectURL(url); };
      await audio.play();
    } catch { setPreviewingVoice(null); }
  };

  const [form, setForm] = useState({
    name: '',
    system_prompt: 'You are a helpful voice assistant. Be concise, friendly, and professional.',
    welcome_message: 'Hello! How can I help you today?',
    voice_id: '21m00Tcm4TlvDq8ikWAM',
    voice_name: 'Rachel',
    widget_color: '#00D67E',
    widget_position: 'bottom-right',
    widget_title: 'Voice Assistant',
  });

  useEffect(() => {
    if (isEdit) {
      (async () => {
        try {
          const { data } = await api().get(`/api/agents/${id}`);
          setForm({
            name:            data.name            || '',
            system_prompt:   data.system_prompt   || '',
            welcome_message: data.welcome_message || '',
            voice_id:        data.voice_id        || '21m00Tcm4TlvDq8ikWAM',
            voice_name:      data.voice_name      || 'Rachel',
            widget_color:    data.widget_color    || '#00D67E',
            widget_position: data.widget_position || 'bottom-right',
            widget_title:    data.widget_title    || 'Voice Assistant',
          });
        } catch {
          setLoadError('Could not load agent. Please try again.');
        }
      })();
    }
  }, [id, isEdit, api]);

  const update = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleVoiceChange = (voiceId) => {
    const voice = DEFAULT_VOICES.find(v => v.id === voiceId);
    setForm(prev => ({ ...prev, voice_id: voiceId, voice_name: voice?.name || '' }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Agent name is required'); setStep(0); return; }
    setError('');
    setSaving(true);
    try {
      if (isEdit) {
        await api().put(`/api/agents/${id}`, form);
        navigate('/dashboard');
      } else {
        const res = await api().post('/api/agents', form);
        navigate(`/dashboard/agents/${res.data.id}`, { state: { justCreated: true } });
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save agent. Check your connection.');
      setSaving(false);
    }
  };

  const canNext = step === 0 ? !!form.name.trim() : true;

  if (loadError) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <p className="text-sm mb-4" style={{ color: 'var(--color-text-secondary)' }}>{loadError}</p>
        <button onClick={() => navigate('/dashboard')} className="btn-ghost text-sm flex items-center gap-1">
          <ArrowLeft size={15} /> Back to Agents
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      {/* Back */}
      <button onClick={() => navigate('/dashboard')} data-testid="back-to-agents"
        className="flex items-center gap-1.5 text-sm mb-6 transition-colors"
        style={{ color: 'var(--color-text-muted)' }}>
        <ArrowLeft size={15} />
        Back to Agents
      </button>

      <div className="mb-8">
        <h1 data-testid="agent-builder-heading"
          className="font-heading text-2xl font-bold tracking-tight"
          style={{ color: 'var(--color-text-primary)' }}>
          {isEdit ? 'Edit Agent' : 'Create New Agent'}
        </h1>
        <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
          {isEdit ? "Update your agent's settings below." : 'Configure your voice agent in three steps.'}
        </p>
      </div>

      {/* Step tabs */}
      <div className="flex items-center gap-0 mb-8">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const done = i < step;
          const active = i === step;
          return (
            <React.Fragment key={s.id}>
              <button
                onClick={() => i < step || (i === step + 1 && canNext) ? setStep(i) : null}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all"
                style={{
                  background: active ? 'var(--color-accent-dim)' : done ? 'transparent' : 'transparent',
                  color: active ? 'var(--color-accent)' : done ? 'var(--color-text-secondary)' : 'var(--color-text-muted)',
                  border: active ? '1px solid rgba(0,214,126,0.2)' : '1px solid transparent',
                  cursor: done ? 'pointer' : active ? 'default' : 'default',
                }}>
                {done
                  ? <Check size={14} style={{ color: 'var(--color-accent)' }} />
                  : <Icon size={14} />}
                {s.label}
              </button>
              {i < STEPS.length - 1 && (
                <div className="w-8 h-px mx-1" style={{ background: 'var(--color-border)' }} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Error banner */}
      {error && (
        <div data-testid="agent-builder-error"
          className="mb-6 text-sm rounded-xl px-4 py-3"
          style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', color: '#f87171' }}>
          {error}
        </div>
      )}

      {/* Two-column layout: form + preview */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

        {/* ── Form column ── */}
        <div className="lg:col-span-3">

          {/* Step 0 — Basics */}
          {step === 0 && (
            <div className="rounded-2xl p-6 space-y-5"
              style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
              <p className="text-xs font-semibold tracking-widest uppercase" style={{ color: 'var(--color-text-muted)' }}>
                Step 1 — Identity
              </p>

              <Field label="Agent Name" hint="How this agent appears in your dashboard.">
                <input
                  data-testid="agent-name-input"
                  value={form.name}
                  onChange={e => update('name', e.target.value)}
                  className="input-field focus-ring-accent"
                  placeholder="e.g. Sales Assistant, Support Bot"
                  required
                />
              </Field>

              <Field label="System Prompt" hint="Instructions that define this agent's behaviour and personality.">
                <textarea
                  data-testid="agent-prompt-input"
                  value={form.system_prompt}
                  onChange={e => update('system_prompt', e.target.value)}
                  rows={5}
                  className="input-field focus-ring-accent resize-none"
                  placeholder="You are a helpful sales assistant for Acme Corp. Be friendly, concise, and always offer to book a demo..."
                />
              </Field>

              <Field label="Welcome Message" hint="The first thing the agent says when someone opens the widget.">
                <input
                  data-testid="agent-welcome-input"
                  value={form.welcome_message}
                  onChange={e => update('welcome_message', e.target.value)}
                  className="input-field focus-ring-accent"
                  placeholder="Hey! How can I help you today?"
                />
              </Field>
            </div>
          )}

          {/* Step 1 — Voice */}
          {step === 1 && (
            <div className="rounded-2xl p-6"
              style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
              <p className="text-xs font-semibold tracking-widest uppercase mb-5" style={{ color: 'var(--color-text-muted)' }}>
                Step 2 — Voice
              </p>
              <div className="grid grid-cols-2 gap-2.5">
                {DEFAULT_VOICES.map(v => (
                  <div
                    key={v.id}
                    role="button"
                    tabIndex={0}
                    data-testid={`voice-option-${v.name.toLowerCase()}`}
                    onClick={() => handleVoiceChange(v.id)}
                    onKeyDown={(e) => e.key === 'Enter' && handleVoiceChange(v.id)}
                    className="text-left p-3.5 rounded-xl border transition-all cursor-pointer"
                    style={{
                      background: form.voice_id === v.id ? 'var(--color-accent-dim)' : 'var(--color-elevated)',
                      borderColor: form.voice_id === v.id ? 'rgba(0,214,126,0.35)' : 'var(--color-border)',
                    }}>
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                        {v.name}
                      </span>
                      <span className="text-xs px-1.5 py-0.5 rounded font-mono"
                        style={{ background: 'var(--color-border)', color: 'var(--color-text-muted)' }}>
                        {v.gender}
                      </span>
                    </div>
                    <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{v.style}</span>
                    <div className="mt-2 flex items-center gap-2">
                      {form.voice_id === v.id && (
                        <div className="flex items-center gap-1">
                          <Check size={11} style={{ color: 'var(--color-accent)' }} />
                          <span className="text-xs font-medium" style={{ color: 'var(--color-accent)' }}>Selected</span>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={(e) => previewVoice(e, v.id)}
                        className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-md transition-all ml-auto"
                        style={{
                          background: previewingVoice === v.id ? 'rgba(0,214,126,0.12)' : 'transparent',
                          color: previewingVoice === v.id ? 'var(--color-accent)' : 'var(--color-text-muted)',
                          border: '1px solid var(--color-border)',
                        }}>
                        <Volume2 size={9} />
                        {previewingVoice === v.id ? 'Stop' : 'Play'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 2 — Widget */}
          {step === 2 && (
            <div className="rounded-2xl p-6 space-y-5"
              style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
              <p className="text-xs font-semibold tracking-widest uppercase" style={{ color: 'var(--color-text-muted)' }}>
                Step 3 — Widget Appearance
              </p>

              <Field label="Widget Title">
                <input
                  data-testid="widget-title-input"
                  value={form.widget_title}
                  onChange={e => update('widget_title', e.target.value)}
                  className="input-field focus-ring-accent"
                  placeholder="Voice Assistant"
                />
              </Field>

              <Field label="Accent Color">
                <div className="flex items-center gap-3 flex-wrap">
                  {PRESET_COLORS.map(c => (
                    <button key={c} type="button" onClick={() => update('widget_color', c)}
                      className="w-8 h-8 rounded-full transition-transform hover:scale-110 relative"
                      style={{ background: c, boxShadow: form.widget_color === c ? `0 0 0 2px var(--color-base), 0 0 0 4px ${c}` : 'none' }}>
                      {form.widget_color === c && (
                        <Check size={13} className="absolute inset-0 m-auto" color="rgba(0,0,0,0.7)" />
                      )}
                    </button>
                  ))}
                  <div className="flex items-center gap-2 ml-1">
                    <input
                      type="color"
                      value={form.widget_color}
                      onChange={e => update('widget_color', e.target.value)}
                      className="w-8 h-8 rounded-full cursor-pointer border-0 bg-transparent"
                      data-testid="widget-color-input"
                      title="Custom color"
                    />
                    <input
                      value={form.widget_color}
                      onChange={e => update('widget_color', e.target.value)}
                      className="input-field focus-ring-accent font-mono text-xs w-28"
                      placeholder="#00D67E"
                    />
                  </div>
                </div>
              </Field>

              <Field label="Position">
                <div className="flex gap-3">
                  {['bottom-right', 'bottom-left'].map(pos => (
                    <button
                      key={pos}
                      type="button"
                      data-testid={`widget-position-${pos}`}
                      onClick={() => update('widget_position', pos)}
                      className="flex-1 py-2.5 rounded-xl text-sm font-medium transition-all border"
                      style={{
                        background: form.widget_position === pos ? 'var(--color-accent-dim)' : 'var(--color-elevated)',
                        borderColor: form.widget_position === pos ? 'rgba(0,214,126,0.35)' : 'var(--color-border)',
                        color: form.widget_position === pos ? 'var(--color-accent)' : 'var(--color-text-secondary)',
                      }}>
                      {pos === 'bottom-right' ? '↘ Bottom Right' : '↙ Bottom Left'}
                    </button>
                  ))}
                </div>
              </Field>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-5">
            <button
              type="button"
              onClick={() => step > 0 ? setStep(s => s - 1) : navigate('/dashboard')}
              className="btn-ghost flex items-center gap-1.5 text-sm">
              <ArrowLeft size={15} />
              {step === 0 ? 'Cancel' : 'Back'}
            </button>

            {step < STEPS.length - 1 ? (
              <button
                type="button"
                onClick={() => canNext && setStep(s => s + 1)}
                disabled={!canNext}
                className="btn-accent flex items-center gap-1.5 text-sm disabled:opacity-40 disabled:cursor-not-allowed">
                Next
                <ArrowRight size={15} />
              </button>
            ) : (
              <button
                data-testid="save-agent-btn"
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="btn-accent flex items-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed">
                {saving
                  ? <><span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />Saving...</>
                  : <><Save size={14} />{isEdit ? 'Update Agent' : 'Create Agent'}</>
                }
              </button>
            )}
          </div>
        </div>

        {/* ── Preview column ── */}
        <div className="lg:col-span-2 hidden lg:block">
          <div className="sticky top-6">
            <p className="text-xs font-semibold tracking-widest uppercase mb-3"
              style={{ color: 'var(--color-text-muted)' }}>
              Live Preview
            </p>
            <WidgetPreview form={form} />
            <p className="text-xs mt-3 text-center" style={{ color: 'var(--color-text-muted)' }}>
              Voice: <span style={{ color: 'var(--color-text-secondary)' }}>{form.voice_name || 'Rachel'}</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
